import React, { useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { AmcContractRules, canActOnAmcContract } from "features/Amc/rules";
import { completeAmc } from "features/Amc/services/AmcService";
import type { Amc } from "features/Amc/types/amc.types";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import { ClientAdvanceSourceType } from "features/ClientAdvances/constants/client-advance.constants";

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

type Props = {
  contract: Amc;
  onSuccess: () => void | Promise<void>;
};

export default function AmcCompletionActions({ contract, onSuccess }: Props) {
  const { can } = useAccess();
  const { isYou, isSuperAdmin } = useProfile();
  const canComplete =
    can(Permissions.AMC.COMPLETE) &&
    AmcContractRules.canComplete(contract.status as any) &&
    canActOnAmcContract(contract, isYou, isSuperAdmin);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeSubmitting, setCompleteSubmitting] = useState(false);

  if (!canComplete) return null;

  const handleComplete = async () => {
    setCompleteSubmitting(true);
    try {
      const res = await completeAmc(contract.id);
      if (res.success) {
        toast.success(res.message ?? "Contract marked as completed.");
        setCompleteOpen(false);
        await onSuccess();
      } else {
        toast.error(res.message ?? "Could not complete this contract.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not complete this contract."));
    } finally {
      setCompleteSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-success d-inline-flex align-items-center gap-1"
        onClick={() => setCompleteOpen(true)}
      >
        <i className="ri-checkbox-circle-line align-bottom" />
        Complete
      </button>

      {/* Complete — confirmation only, no fields */}
      <Modal
        isOpen={completeOpen}
        toggle={() => !completeSubmitting && setCompleteOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !completeSubmitting && setCompleteOpen(false)}>
          Complete contract
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            This marks the contract as <strong>Completed</strong>. Blocked if
            any linked visit is still planned or in progress.
          </p>
          <p className="text-muted small mb-3">
            Agreed amount:{" "}
            <span className="text-body fw-medium">
              {contract.agreedAmount != null
                ? formatCurrency(contract.agreedAmount)
                : "—"}
            </span>
            {contract.finalAmount != null ? (
              <>
                {" "}
                — final amount:{" "}
                <span className="text-body fw-medium">
                  {formatCurrency(contract.finalAmount)}
                </span>
              </>
            ) : null}
          </p>
          <LinkedClientAdvancesTable
            sourceType={ClientAdvanceSourceType.AMC}
            sourceId={contract.id}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={completeSubmitting}
            onClick={() => setCompleteOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="success"
            disabled={completeSubmitting}
            onClick={() => void handleComplete()}
          >
            {completeSubmitting ? "Please wait…" : "Complete"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
